import React from 'react';
import { t } from '@lingui/core/macro';
import { Button, Input } from 'src/components/ui';
import Icon from 'src/components/Icon';
import Notification from 'src/components/Notification';
import { Field, FieldRow, InfoTip, SectionCard, TextField } from 'src/components/forms';
import { OrgUnitRecord } from './orgUnitModel';

export type IdentifiersSectionProps = {
  draft: OrgUnitRecord;
  setField: (path: string, value: unknown) => void;
  errorFor: (path: string) => string | undefined;
  isNew: boolean;
};

export function IdentifiersSection({ draft, setField, errorFor, isNew }: IdentifiersSectionProps) {
  const entityIdentifierHelp = t`The institution's public identifier, as issued and published by OCIF. Enter it exactly as OCIF wrote it; FIRE does not check its format, because the format is OCIF's to decide.
It must be unique across institutions, and the save is refused (naming the other institution) if it is already taken.
It is editable: nothing in FIRE joins on it, so correcting a mistyped or reissued ID is safe and changes nothing else.`;
  const parentEntityHelp = t`The group of institutions this one belongs to, if any: for example a holding company or a parent bank whose subsidiaries report separately.
It is a label used to roll several institutions up in analytics, so institutions are meant to SHARE a value: type it exactly as the other members of the group have it. It does not change where the institution sits in the hierarchy, and leaving it blank is fine.`;
  const cfiidHelp = t`FIRE's own system code for this institution, assigned automatically when the institution was created. It is three letters and five digits (e.g. FIR00001).
It is the key that this institution's facts, its migrated history and its data-access grants all join on, which is why it cannot be changed here or anywhere else.
Older institutions carry a legacy OCIF-issued code in this field instead (DBD00001, IFE00003, ...). Nothing derives meaning from the three letters.`;

  return (
    <SectionCard span={5} title={t`Identifiers`}>
      {isNew ? (
        <Notification color="info">
          {t`The Institution ID is the public identifier issued by OCIF; enter the one OCIF published for this institution. It stays editable afterwards.`}
        </Notification>
      ) : (
        <FieldRow>
          <Field
            label={
              <>
                {t`System Code (CFIID)`}
                <InfoTip id="orgunit-cfiid-info" text={cfiidHelp} />
              </>
            }
            htmlFor="orgunit-cfiid"
            width="sm"
          >
            <div className="flex items-center gap-1 mb-4">
              <Input id="orgunit-cfiid" readOnly value={draft.code ?? ''} className="font-mono field-sizing-content" size={1} />
              <Button color="link" aria-label={t`Copy`} onClick={() => navigator.clipboard?.writeText(String(draft.code ?? ''))}>
                <Icon icon="Copy" />
              </Button>
            </div>
          </Field>
        </FieldRow>
      )}
      <FieldRow>
        <TextField
          id="orgunit-entity-identifier"
          label={
            <>
              {t`Institution ID`}
              <InfoTip id="orgunit-entity-identifier-info" text={entityIdentifierHelp} />
            </>
          }
          required={isNew}
          width="sm"
          value={draft.entityIdentifier}
          error={errorFor('entityIdentifier')}
          onChange={value => setField('entityIdentifier', value)}
        />
        <TextField
          id="orgunit-licence-number"
          label={t`License Number`}
          required={isNew}
          width="sm"
          value={draft.licenceNumber}
          error={errorFor('licenceNumber')}
          onChange={value => setField('licenceNumber', value)}
        />
        <TextField
          id="orgunit-parent-entity"
          label={
            <>
              {t`Parent Entity`}
              <InfoTip id="orgunit-parent-entity-info" text={parentEntityHelp} />
            </>
          }
          width="lg"
          value={draft.parentEntity}
          onChange={value => setField('parentEntity', value)}
        />
      </FieldRow>
    </SectionCard>
  );
}

export default IdentifiersSection;
